import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import SuccessNotification from '../components/SuccessNotification';
import { publicAPI } from '../services/apiService';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isConsentGiven, setIsConsentGiven] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    contact_email: 'info@fotografx.de',
    contact_phone: '+49 (0) 123 456 789',
    contact_address: 'Berlin, Deutschland',
    contact_hours: 'Mo-Fr 09:00-18:00, Sa 10:00-16:00'
  });

  useEffect(() => {
    loadContactInfo();
  }, []);

  const loadContactInfo = async () => {
    try {
      const response = await publicAPI.getContactSettings();
      setContactInfo(response.data);
    } catch (error) {
      console.error('Error loading contact info:', error);
    }
  };

  // Parse contact hours string into structured format
  const parseContactHours = (hoursString) => {
    if (!hoursString) return { weekdays: '', saturday: '', sunday: '' };
    
    // Default values
    let weekdays = '';
    let saturday = '';
    let sunday = ''; // Remove hardcoded German text
    
    // Split by comma and process each part
    const parts = hoursString.split(',').map(part => part.trim());
    
    parts.forEach(part => {
      if (part.includes('Mo-Fr') || part.includes('Mo-So')) {
        const match = part.match(/(\d{2}:\d{2})-(\d{2}:\d{2})/);
        if (match) {
          weekdays = `${match[1]} - ${match[2]}`;
        }
      } else if (part.includes('Sa')) {
        const match = part.match(/(\d{2}:\d{2})-(\d{2}:\d{2})/);
        if (match) {
          saturday = `${match[1]} - ${match[2]}`;
        }
      } else if (part.includes('So')) {
        const match = part.match(/(\d{2}:\d{2})-(\d{2}:\d{2})/);
        if (match) {
          sunday = `${match[1]} - ${match[2]}`;
        }
      }
    });
    
    return { weekdays, saturday, sunday };
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Submit contact form to backend API
      const response = await fetch(`${window.location.origin}/api/public/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit contact form');
      }

      // Reset form
      setFormData({ name: '', email: '', message: '' });
      setShowSuccess(true);
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert(t('contact.form.errorMessage', 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut.'));
    }
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20">
        <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1632059368252-be6d65abc4e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0fGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc1NzUwNzM2OHww&ixlib=rb-4.1.0&q=85')`
          }}
        ></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {t('contact.title')}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {t('contact.form.title')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300"
                    placeholder={t('contact.form.namePlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300"
                    placeholder={t('contact.form.emailPlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300 resize-none"
                    placeholder={t('contact.form.messagePlaceholder')}
                  />
                </div>
                
                {/* Consent Checkbox */}
                <div className="bg-gray-900/50 border border-gray-700 p-4 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isConsentGiven}
                      onChange={(e) => setIsConsentGiven(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-white"
                      required
                    />
                    <span className="text-sm text-gray-300">
                      {t('contact.form.consent', 'Ich stimme der Verarbeitung meiner personenbezogenen Daten zu und habe die Datenschutzerklärung gelesen.')}
                      {' '}
                      <a href="/datenschutz" className="text-blue-400 hover:underline">
                        {t('contact.form.privacyLink', 'Datenschutzerklärung')}
                      </a>
                    </span>
                  </label>
                </div>
                
                <button
                  type="submit"
                  disabled={!isConsentGiven}
                  className={`w-full px-6 py-4 font-semibold transition-all duration-300 flex items-center justify-center ${
                    isConsentGiven 
                      ? 'bg-white text-black hover:bg-gray-200' 
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Send className="h-5 w-5 mr-2" />
                  {t('contact.form.send')}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {t('contact.info.title')}
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-900 border border-gray-700 flex items-center justify-center">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 
                      className="text-lg font-semibold mb-2"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      {t('contact.info.email')}
                    </h3>
                    <p className="text-gray-400">{contactInfo.contact_email}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-900 border border-gray-700 flex items-center justify-center">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 
                      className="text-lg font-semibold mb-2"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      {t('contact.info.phone')}
                    </h3>
                    <p className="text-gray-400">{contactInfo.contact_phone}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-900 border border-gray-700 flex items-center justify-center">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 
                      className="text-lg font-semibold mb-2"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      {t('contact.info.address')}
                    </h3>
                    <p className="text-gray-400">{contactInfo.contact_address}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-900 border border-gray-700 flex items-center justify-center">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 
                      className="text-lg font-semibold mb-3"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      {t('contact.info.hours')}
                    </h3>
                    <div className="space-y-1 text-gray-400 text-sm">
                      {(() => {
                        const parsedHours = parseContactHours(contactInfo.contact_hours);
                        return (
                          <>
                            <div className="flex justify-between items-center">
                              <span>{t('contact.businessHours.weekdays')}:</span>
                              <span className="font-medium">{parsedHours.weekdays || '9:00 - 18:00'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>{t('contact.businessHours.saturday')}:</span>
                              <span className="font-medium">{parsedHours.saturday || '10:00 - 16:00'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span>{t('contact.businessHours.sunday')}:</span>
                              <span className="font-medium">{parsedHours.sunday || t('contact.businessHours.byAppointment')}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="mt-8 p-4 bg-gray-900 border-l-4 border-white">
                <p className="text-sm text-gray-300">
                  <strong>{t('contact.responseTime.title')}:</strong> {t('contact.responseTime.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Success Notification */}
      <SuccessNotification
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title={t('contact.form.successTitle', 'Nachricht gesendet!')}
        message={t('contact.form.successMessage', 'Ich melde mich bald bei Ihnen.')}
      />
    </div>
  );
};

export default Contact;